/**
 * Date formatting utilities for frontend
 * Mirrors backend date formatting logic
 */

/**
 * Format date to DD/MM/YYYY format for display
 * @param {string|Date} value - Date value to format
 * @returns {string|null} - Formatted date string or null
 */
export function formatDateToDDMMYYYY(value) {
  if (!value) return null;
 
  // Handle YYYY-MM-DD format (from VARCHAR storage)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [yyyy, mm, dd] = value.split(/[-T]/);
    return `${dd}/${mm}/${yyyy}`;
  }
 
  // Handle DD/MM/YYYY format (already formatted)
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    return value;
  }
 
  // Handle Date objects or ISO strings
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
 
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
 
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Parse date for database storage
 * @param {string} value - Date value to parse
 * @returns {string|null} - Parsed date for DB or null
 */
export function parseDateForDB(value) {
  if (!value) return null;
  
  // Handle DD/MM/YYYY format
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    const [dd, mm, yyyy] = value.split('/');
    return `${yyyy}-${mm}-${dd}`;
  }
  
  // Handle ISO format
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  
  return d.toISOString().split('T')[0];
}

/**
 * Check if a field name represents a date field
 * @param {string} fieldName - Field name to check
 * @returns {boolean} - True if it's a date field
 */
export function isDateField(fieldName) {
  const dateFieldNames = [
    'dob', 'dateofbirth', 'date', 'dateoffiling', 'dateoflibility',
    'dateofvalidityfrom', 'dateofvalidityto', 'dateofissue',
    'dateofincorporation', 'dateofcommencement', 'dateofudyamregistration',
    'accountopeningdate', 'oldestOpenDate', 'recentOpenDate',
    'mostRecentEnquiryDate', 'reportDate', 'loanApplicationRegistrationDate'
  ];
  const lowerField = fieldName.toLowerCase().replace(/[_\s]/g, '');
  return dateFieldNames.some(df => lowerField.includes(df));
}